import type { ApiClient, ApiResult, QueryValue, RequestOptions } from "../client.ts";
import type { IdempotencyKey } from "../idempotency.ts";
import type { BodyOf, HttpMethod, OperationAt, PathParamsOf, PathsFor, QueryParamsOf, ResponseOf } from "../schema.ts";

/**
 * T075, `contracts/client-api.md` § Layers: one typed call into `request()` with the
 * generated types applied. The path template's `{name}` segments are filled from the
 * operation's own path parameters, the query from its query parameters, the body from
 * its request body, and the result carries its success response. Nothing here widens a
 * type: an incompatible contract change fails at the call site.
 */
export interface CallOptions<O> {
  readonly params?: PathParamsOf<O>;
  readonly query?: QueryParamsOf<O>;
  readonly body?: BodyOf<O>;
  readonly version?: number;
  readonly unversioned?: true;
  readonly idempotencyKey?: IdempotencyKey;
  readonly signal?: AbortSignal;
  readonly retry?: boolean;
  readonly onUploadProgress?: (loaded: number, total: number) => void;
}

export function fillPath(template: string, params: Readonly<Record<string, unknown>> | undefined): string {
  return template.replace(/\{([^}]+)\}/g, (_, name: string) => {
    const value = params?.[name];
    if (typeof value !== "string" && typeof value !== "number") throw new Error(`path ${template} needs the parameter ${name}`);
    return encodeURIComponent(String(value));
  });
}

/** The entries of an options object that were given, so an absent option stays absent. */
function given<T extends object>(source: T): { [K in keyof T]-?: Exclude<T[K], undefined> } {
  return Object.fromEntries(Object.entries(source).filter(([, value]) => value !== undefined)) as { [K in keyof T]-?: Exclude<T[K], undefined> };
}

export async function call<M extends HttpMethod, P extends PathsFor<M>>(
  client: ApiClient,
  method: M,
  path: P,
  options: CallOptions<OperationAt<P, M>> = {},
): Promise<ApiResult<ResponseOf<OperationAt<P, M>>>> {
  const { params, query, body, ...rest } = options;
  const request: RequestOptions = {
    method: method.toUpperCase() as Uppercase<M>,
    path: fillPath(path, params),
    ...given({ query, body }),
    ...given(rest),
  };
  return client.request<ResponseOf<OperationAt<P, M>>>(request);
}

/**
 * The options of a `GET` whose answer is a file: the path's typed parameters and the query as its
 * pairs. The query is not narrowed to the operation's declared parameters because a file route's
 * query may carry parameters the document names by a pattern (`label[<value>]`, `field[<id>]`),
 * which no declared member can spell.
 */
export interface DownloadOptions<O> {
  readonly params: PathParamsOf<O>;
  readonly query: Readonly<Record<string, QueryValue>>;
  readonly signal?: AbortSignal;
}

/** One typed `GET` whose success answer is read as a `Blob` of `accept`'s type, under the `download` kind's timeout; a problem is thrown as any call's is. */
export async function download<P extends PathsFor<"get">>(client: ApiClient, path: P, accept: string, options: DownloadOptions<OperationAt<P, "get">>): Promise<ApiResult<Blob>> {
  const { params, query, signal } = options;
  return client.request<Blob>({ method: "GET", path: fillPath(path, params), query, accept, response: "blob", kind: "download", ...given({ signal }) });
}

/** An operation group: a bound `call` per route, named as 001 `contracts/http-api.md` names them. */
export function group<T>(build: (c: typeof call) => T): T {
  return build(call);
}
